import {Component, inject, OnDestroy, OnInit} from '@angular/core';
import {PermissionService} from "../../../_auth/permission.service";
import {EventNewModel} from "../_models/event-new-model";
import {WeekdayLabels, Weekdays} from "../../../x-models/x-enums/weekdays";
import {IntervalLabels, Intervals} from "../../../x-models/x-enums/intervals";
import {LocationHttpService} from "../../../http-requests/location-http.service";
import {LocationDto} from "../../../models/location-dto";
import {GlobalAlertService} from "../../../_services/global-alert.service";
import {AlertLevel} from "../../../enums/alert-level";
import {ConfigurationHttpService} from "../../../http-requests/configuration-http.service";
import {ConfigurationDto} from "../../../models/configuration-dto";
import {EventHttpService} from "../../../management/x-http-requests/event-http.service";
import {EventDto} from "../../../models/event/event-dto";
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  finalize,
  Observable,
  of,
  Subject,
  Subscription,
  switchMap
} from "rxjs";
import {ActivatedRoute, Router} from "@angular/router";
import {SelectGroup, SelectionMode} from "../../../models/_generic/select-group";
import {SupplierDto} from "../../company/x-models/supplier-dto";
import {HttpEventType} from "@angular/common/http";
import {AppRoutes} from "../../../enums/routes";
import {LocationRoutes} from "../_models/_enums/location-routes";
import {DateFormat, DateFormatLabels} from "../../../x-models/x-enums/date-format";
import {EventCreationLocationLinker, EventCreationModel, EventCreationOptions} from "../_models/event-creation-model";
import {CommonModule, DatePipe} from "@angular/common";
import {SupplierPopupComponent} from "../../company/x-popups/supplier-popup/supplier-popup.component";
import {ConfirmPopupComponent} from "../../../_components/confirm-popup/confirm-popup.component";
import {WeekdayViewComponent} from "../_components/weekday-view/weekday-view.component";
import {PageScrollableComponent} from "../../../_components/page-scrollable/page-scrollable.component";
import {FormContainerComponent} from "../../../_components/form-container/form-container.component";
import {HeaderContainerComponent} from "../../../_components/header-container/header-container.component";
import {PageContainerComponent} from "../../../_components/page-container/page-container.component";
import {FormsModule} from "@angular/forms";
import {FormExpandableComponent} from "../../../_components/form-expandable/form-expandable.component";
import {NgOptionComponent, NgSelectComponent} from "@ng-select/ng-select";
import {LoadingComponent} from "../../x-components/loading/loading.component";
import {CompanyTimeZoneService} from "../../../_shared/time/company-time-zone.service";
import {
  addCalendarDays,
  calendarDaysBetween,
  CompanyWallClock,
  companyDayKey,
  companyLocalTimeInstant,
  companyWallClock,
  dayKeyToPickerDate,
  mondayBasedWeekday,
  parseDateTimeLocalValue,
  pickerDateToDayKey,
  toDateTimeLocalValue
} from "../../../_shared/time/company-day";
import {SegmentedChoiceComponent} from "../../../_ui/components/controls/segmented-choice/segmented-choice.component";
import {InlineNoticeComponent} from "../../../_ui/components/feedback/inline-notice/inline-notice.component";
import {EventKind, EventKindLabels} from "../../../enums/event-kind";
import {EventCreationAllowance} from "../../../models/event/event-creation-allowance";
import {
  describeEventCreationError,
  describeEventCreationRefusal,
  EVENT_CREATION_FALLBACK_MESSAGE
} from "./event-creation-errors";
import {EventCreationVerdict} from "../_models/event-creation-verdict";

/**
 * One segment of the "Art des Events" picker: the wire value and the words on it.
 *
 * Declared here rather than reaching for `SegmentedChoiceOption<EventKind>` directly — it is structurally the
 * same shape, and a local name keeps the component's own vocabulary readable at the call site.
 */
interface EventKindOption {
  value: EventKind;
  label: string;
}

@Component({
  selector: 'app-events',
  templateUrl: './events.component.html',
  imports: [
    CommonModule,
    FormsModule,
    SupplierPopupComponent,
    ConfirmPopupComponent,
    WeekdayViewComponent,
    PageScrollableComponent,
    FormContainerComponent,
    HeaderContainerComponent,
    PageContainerComponent,
    FormExpandableComponent,
    NgSelectComponent,
    NgOptionComponent,
    DatePipe,
    LoadingComponent,
    SegmentedChoiceComponent,
    InlineNoticeComponent
  ],
  providers: [DatePipe],
  styleUrl: './events.component.scss'
})
export class EventsComponent implements OnInit, OnDestroy {
  newEvent: EventNewModel = new EventNewModel();
  weekdays = Object.keys(Weekdays).slice(0, Object.entries(Weekdays).length / 2);
  intervals = Object.keys(Intervals).slice(0, Object.entries(Intervals).length / 2);
  locations: LocationDto[] | undefined;
  configurations: ConfigurationDto[] | undefined;
  protected readonly WeekdayLabels = WeekdayLabels;
  protected readonly parseInt = parseInt;
  protected readonly Weekdays = Weekdays;
  protected readonly IntervalLabels = IntervalLabels;
  protected readonly formats = Object.keys(DateFormat).slice(0, Object.entries(DateFormat).length / 2);
  protected new_events_grouped: { [key: number]: EventCreationModel[] } | undefined;
  protected first: number = 0;
  protected last: number = 10;
  protected counts: number[] = [10, 25, 50, 100, 2000000000]
  protected events: EventDto[] | undefined | null = undefined;
  protected search: string = "";
  /** Sentinel page size that requests every event (mirrors the "Alle" option in {@link counts}). */
  private readonly SEARCH_ALL_LIMIT = 2000000000;
  protected selection: SelectGroup<EventDto> = new SelectGroup<EventDto>();
  protected supplierPopup_isVisible: boolean = false;
  protected download_combined: boolean = true;
  protected readonly location = location;
  protected readonly SelectionMode = SelectionMode;
  protected readonly DateFormatLabels = DateFormatLabels;
  private events_full: EventDto[] | undefined | null = undefined;
  /** Full, unpaginated event set used to search across *all* events (lazily loaded, cached). */
  private searchAll: EventDto[] | undefined | null = undefined;
  private searchAllLoading: boolean = false;
  /** Debounced pipe for search-box input so rapid keystrokes filter once, not per character. */
  private readonly search$ = new Subject<string>();
  private searchSub?: Subscription;
  private previous: string | undefined;
  private readonly companyTimeZone = inject(CompanyTimeZoneService);

  // ---------------------------------------------------------------------------------------------------
  // #664 — the contract-gated "Art des Events" picker. Everything below is self-contained: it reads the
  // form's existing `newEvent.start`/`.end` and never reshapes them.
  // ---------------------------------------------------------------------------------------------------

  /** The two segments of the "Art des Events" picker, built once. Value + label, in display order. */
  protected readonly kindOptions: EventKindOption[] = [
    {value: EventKind.Real, label: EventKindLabels[EventKind.Real]},
    {value: EventKind.Virtual, label: EventKindLabels[EventKind.Virtual]}
  ];
  protected readonly EventKind = EventKind;
  /** The server's verdict for the current start/end window; `undefined` until the first answer arrives. */
  protected allowance: EventCreationAllowance | undefined;
  /** True while an allowance request is in flight — the hint line then says so instead of going blank. */
  protected allowanceLoading: boolean = false;
  /** The dry-run verdicts of the series preview, keyed by the very model objects the preview renders. */
  protected dryRunVerdicts: Map<EventCreationModel, EventCreationVerdict> | undefined;
  /**
   * Debounced pipe for start/end changes. Typing into a `datetime-local` field emits per keystroke (and per
   * arrow-key nudge), and every emission would otherwise be one allowance round-trip — the same reason the
   * search box upstream is debounced (Bug #146).
   */
  private readonly allowanceWindow$ = new Subject<void>();
  private allowanceSub?: Subscription;

  constructor(public PermissionService: PermissionService,
              private gas: GlobalAlertService,
              private route: ActivatedRoute,
              private router: Router,
              private locationHttp: LocationHttpService,
              private configurationHttp: ConfigurationHttpService,
              private eventHttp: EventHttpService,
              private datePipe: DatePipe) {
  }

  protected get canConfigureLocations(): boolean {
    return this.locations !== undefined && this.configurations !== undefined;
  }

  protected get allowedConfigureLocations(): boolean {
    return this.PermissionService.CheckPermission(this.PermissionService.CombinedPermission_EventAddConfigured());
  }

  protected get new_events_grouped_visible(): boolean {
    return this.new_events_grouped !== undefined;
  }

  protected get hasDownloadPermission(): boolean {
    return this.PermissionService.CheckPermission(this.PermissionService.CombinedPermission_EventDownloadExtendedEvent())
  }

  protected get hasExtendedPermission(): boolean {
    return this.PermissionService.CheckPermission(this.PermissionService.CombinedPermission_EventGetExtendedEvent())
  }

  ngOnInit(): void {
    // Resolve the company zone up front: the Start/Ende inputs show and read its wall clock.
    this.companyTimeZone.load().subscribe();

    // Debounce search-box input: rapid typing settles into a single filter pass instead of one per
    // keystroke. distinctUntilChanged drops no-op re-emits (e.g. the same value from two-way binding).
    this.searchSub = this.search$.pipe(
      debounceTime(200),
      distinctUntilChanged()
    ).subscribe(value => this.applyFilter(value));

    // #664: same shape, one window per settled edit rather than one per keystroke.
    //
    // `switchMap`, NOT a `subscribe` that starts a request of its own: with one subscription per emission an
    // earlier, slower GET can still land after a later one, and {@link applyAllowance} would then reconcile the
    // form against a window the administrator no longer has — flipping the kind back. `switchMap` cancels the
    // in-flight request the moment a newer window is asked about, so the LAST window always wins.
    this.allowanceSub = this.allowanceWindow$.pipe(
      debounceTime(300),
      switchMap(() => this.allowanceFor())
    ).subscribe(allowance => {
      if (allowance === undefined) this.allowance = undefined;
      else this.applyAllowance(allowance);
    });

    this.preload(() => {
      this.load();
    });
  }

  ngOnDestroy(): void {
    this.searchSub?.unsubscribe();
    this.allowanceSub?.unsubscribe();
  }

  clear() {
    this.newEvent = new EventNewModel();
    // #664: the allowance belongs to the window that was just discarded, so it must go with it — a stale
    // "0 von 1 frei" under a freshly reset form is worse than no line at all.
    this.allowance = undefined;
    this.dryRunVerdicts = undefined;
  }

  /**
   * The Start/Ende `datetime-local` value: the instant's wall clock in the COMPANY zone. The old
   * `InputDate.input_datetime` binding read and wrote browser-local fields, so an admin in London saw a Berlin event
   * at 20:00 as 19:00 and, typing 20:00, created it an hour late.
   */
  protected wallClockValue(instant: Date): string {
    return toDateTimeLocalValue(instant, this.companyTimeZone.timeZone());
  }

  /** Stores a typed company wall clock as its instant. A cleared or invalid value keeps the previous instant. */
  protected setWallClockValue(target: Date, value: string): void {
    const instant = parseDateTimeLocalValue(value, this.companyTimeZone.timeZone());
    if (instant) target.setTime(instant.getTime());
  }

  load() {
    this.loadEvents();
  }

  loadCreateData() {
    // #664: the allowance depends only on the window, so ask for it as soon as the form is open — the picker
    // must already be in its final shape when the administrator first looks at it, not one round-trip later.
    this.requestAllowance();

    if (this.PermissionService.CheckPermission(this.PermissionService.CombinedPermission_EventAddConfigured())) {
      this.loadConfigurations();
      this.loadLocations();
    }
  }

  // -----------------------------------------------------------------------------------------------------
  // #664 — allowance
  // -----------------------------------------------------------------------------------------------------

  /** The chosen "Art des Events". Bound through `cqme-segmented-choice`; forced by the allowance where a contract says so. */
  protected get kind(): EventKind {
    return this.newEvent.kind;
  }

  /** True when the event being created is (or was forced to) a virtual one. */
  protected get isVirtual(): boolean {
    return this.newEvent.kind === EventKind.Virtual;
  }

  /**
   * Is the kind picker shown at all?
   *
   * Hidden in exactly one case: the contract leaves a single choice. Rendering a two-option picker whose
   * second option the server will silently override is the failure this whole state exists to avoid. With no
   * allowance yet (not loaded, or the request failed) the picker stays visible with both options — the server
   * runs the same gate on submit, so the worst case is a refusal that names the rule.
   */
  protected get kindSelectorVisible(): boolean {
    return this.allowance !== undefined && this.allowance.allowedKinds.length > 1;
  }

  /**
   * True when a covering contract licenses zero devices, so the server coerces the event to Virtual.
   *
   * Keyed on `realCoerced`, not on "the only allowed kind is Virtual": the allowance also narrows to Virtual
   * when a Kassen-Event already runs in the window, and that case has nothing to do with the contract — see
   * {@link realBlockedByOverlap}. Reading the kind list alone would put the wrong sentence under the form.
   */
  protected get virtualForced(): boolean {
    return this.allowance?.realCoerced === true;
  }

  /**
   * True when only a virtual event may be created because a Kassen-Event already occupies the window on this
   * shard — the contract would allow a real one, the overlap rule does not.
   */
  protected get realBlockedByOverlap(): boolean {
    const allowed = this.allowance?.allowedKinds;
    return allowed !== undefined && allowed.length === 1 && allowed[0] === EventKind.Virtual
      && this.allowance?.realCoerced !== true;
  }

  /** True when nothing at all may be created in this window — the submit is blocked and a reason is shown. */
  protected get creationBlocked(): boolean {
    return this.allowance !== undefined && this.allowance.allowedKinds.length === 0;
  }

  /** True when the server reported a Kassen-Event already overlapping this window on this shard. */
  protected get realOverlapping(): boolean {
    return this.allowance?.realOverlapping === true;
  }

  /** True when a covering contract licenses virtual events and every slot for this window is taken. */
  protected get virtualExhausted(): boolean {
    return this.allowance?.virtualExhausted === true;
  }

  /**
   * „Alle n virtuellen Events in diesem Zeitraum sind belegt." — or `undefined` when that is not the case.
   *
   * Guarded twice over. The server already excludes the two states this sentence would lie about, but the
   * number comes from {@link virtualLimit} on this side, so both are re-checked here: an **unlimited**
   * contract reports `null` and can never be exhausted, and a contract licensing **zero** virtual events was
   * never able to create one — „alle 0 virtuellen Events sind belegt" is noise, not news, and that tenant
   * stays silent exactly as it did before the feature existed.
   */
  private get virtualExhaustedSentence(): string | undefined {
    if (!this.virtualExhausted) return undefined;
    const limit = this.virtualLimit;
    if (limit === null || limit <= 0) return undefined;
    return `Alle ${limit} virtuellen Events in diesem Zeitraum sind belegt.`;
  }

  /**
   * The warning for "only a Kassen-Event is left, and the reason is that the virtual slots ran out".
   *
   * The mirror of {@link realBlockedByOverlap}, and it exists because its absence was silent: the allowance
   * narrowed to `[Real]`, the picker disappeared, and the form went on to create a Kassen-Event for somebody
   * who had come to create a virtual one — with nothing on screen saying so.
   */
  protected get virtualExhaustedText(): string | undefined {
    if (this.creationBlocked) return undefined;

    const kinds = this.allowance?.allowedKinds;
    if (kinds === undefined || kinds.length !== 1 || kinds[0] !== EventKind.Real) return undefined;

    const sentence = this.virtualExhaustedSentence;
    return sentence === undefined ? undefined : `${sentence} Es kann nur ein Kassen-Event angelegt werden.`;
  }

  /**
   * The German sentence behind {@link creationBlocked}; `undefined` while creation is possible.
   *
   * **Built from the allowance's FACTS before falling back to its `refusalCode`**, because the code is one
   * value and the reasons are not. A window can be blocked by an overlapping Kassen-Event *and* have every
   * virtual slot taken; the server has to pick one code, and it prefers the licence one — so a company with
   * two registers and no virtual licence, whose evening was simply already booked, was told „Ihr Vertrag
   * enthält in diesem Zeitraum weder Geräte noch virtuelle Events". That is not what happened, it is not
   * actionable, and the real reason (an overlap they can see in their own list) went unmentioned until the
   * submit came back 409.
   *
   * So: whenever a situational narrowing is reported, the sentence names every one of them and ends with the
   * verdict. Only when none is — a contract gap, or genuinely nothing licensed, both of which the server
   * answers *before* it measures anything — does the contract vocabulary speak, unchanged.
   */
  protected get refusalReason(): string | undefined {
    if (!this.creationBlocked) return undefined;

    const reasons: string[] = [];
    if (this.realOverlapping) reasons.push('In diesem Zeitraum läuft bereits ein Kassen-Event.');
    const exhausted = this.virtualExhaustedSentence;
    if (exhausted !== undefined) reasons.push(exhausted);

    if (reasons.length > 0) return `${reasons.join(' ')} Es kann kein Event angelegt werden.`;

    return describeEventCreationRefusal(this.allowance?.refusalCode, this.allowance?.gapDate)
      ?? EVENT_CREATION_FALLBACK_MESSAGE;
  }

  /**
   * "n von m virtuellen Events frei" — how much of the contract's virtual allowance this window still has.
   *
   * `undefined` when virtual events are not on the table at all, so the line is absent rather than reading
   * "0 von 0" on a company that never licensed them.
   */
  protected get virtualAvailabilityText(): string | undefined {
    const allowance = this.allowance;
    if (allowance === undefined || !allowance.allowedKinds.includes(EventKind.Virtual)) return undefined;

    const remaining = allowance.virtualRemaining === null ? '∞' : `${allowance.virtualRemaining}`;
    const limit = this.virtualLimit === null ? '∞' : `${this.virtualLimit}`;
    return `${remaining} von ${limit} virtuellen Events frei`;
  }

  /**
   * The binding virtual limit across the window: the **smallest** finite limit among the covering contracts,
   * or `null` when every one of them is unlimited (or none were reported).
   *
   * Smallest, not first: an event spanning a boundary has to satisfy every contract it touches, so the one
   * that will refuse first is the one worth naming.
   */
  private get virtualLimit(): number | null {
    const limits = (this.allowance?.contracts ?? [])
      .map(contract => contract.virtualEventCount)
      .filter(limit => limit >= 0);
    return limits.length === 0 ? null : Math.min(...limits);
  }

  /** Bound to the date inputs' `(ngModelChange)` — declared AFTER `[(ngModel)]`, so it reads the new value. */
  protected onWindowChanged(): void {
    this.requestAllowance();
  }

  /**
   * The picker's `(valueChange)`. Normalised here rather than in the template — see the style guide.
   *
   * It changes the kind and NOTHING else. A virtual event carries no location configuration, but the entered
   * price profiles are kept in the model and only hidden ({@link isVirtual} gates the block in the template):
   * picking "Virtuelles Event" used to call `configurationsRemove()`, which is destructive and one-way — the
   * work was gone, switching back to Kassen-Event did not bring it back, and {@link applyAllowance} could
   * trigger it with no user action at all when a slow answer moved the window. The kind is applied to the
   * outgoing batch instead, in {@link createIn}, where it is final.
   */
  protected onKindChanged(value: EventKind | EventKind[] | undefined): void {
    const kind = Array.isArray(value) ? value[0] : value;
    this.newEvent.kind = kind ?? EventKind.Real;
  }

  /** Feeds the debounced pipe. Public-ish so the template can call it from every window control. */
  protected requestAllowance(): void {
    this.allowanceWindow$.next();
  }

  /**
   * The allowance request for the current window, as an observable the one pipeline switches onto.
   *
   * `undefined` stands for "no verdict": an unusable window (nothing is asked) and a failed request (the answer
   * is discarded) both collapse to it, and the subscriber clears {@link allowance} either way.
   *
   * Deliberately silent on failure: this is an advisory read, and a toast on every transient 502 while
   * somebody types a date would be noise. The picker then falls back to "both kinds" and the server's own
   * gate — which runs on submit regardless — produces the message. `catchError` rather than an error callback,
   * because an error reaching the outer pipeline would TEAR IT DOWN and the form would never ask again.
   */
  private allowanceFor(): Observable<EventCreationAllowance | undefined> {
    const start = new Date(this.newEvent.start);
    const end = new Date(this.newEvent.end);
    if (isNaN(start.getTime()) || isNaN(end.getTime()) || start >= end) return of(undefined);

    this.allowanceLoading = true;
    return this.eventHttp.getCreationAllowance(start, end).pipe(
      catchError(err => {
        console.error(err);
        return of(undefined);
      }),
      // Also runs when `switchMap` cancels this request in favour of a newer window — which is exactly right:
      // the spinner belongs to the request that is actually in flight, and the newer one sets it again.
      finalize(() => {
        this.allowanceLoading = false;
      })
    );
  }

  /**
   * Adopts an allowance and reconciles the currently picked kind with it.
   *
   * The reconciliation is the point: a picked Kassen-Event has to become Virtual the moment the window moves
   * into a 0-device period, or the form would show one thing and submit another.
   */
  private applyAllowance(allowance: EventCreationAllowance): void {
    this.allowance = allowance;
    if (allowance.allowedKinds.length === 0) return;

    if (!allowance.allowedKinds.includes(this.newEvent.kind)) this.onKindChanged(allowance.defaultKind);
  }

  loadLocations() {
    this.locationHttp.list().subscribe({
      next: locations => {
        this.locations = locations;
      },
      error: err => {
        this.gas.createAndShow("Fehler", "Fehler beim Laden der Standorte.", AlertLevel.error);
        console.error(err);
        this.locations = undefined;
      }
    })
  }

  loadConfigurations() {
    this.configurationHttp.list().subscribe({
      next: configurations => {
        this.configurations = configurations;
      },
      error: err => {
        this.gas.createAndShow("Fehler", "Fehler beim Laden der Konfigurationen.", AlertLevel.error);
        console.error(err);
        this.configurations = undefined;
      }
    })
  }

  show(event: EventDto) {
    if (!this.hasExtendedPermission || this.selection.selectionMode == SelectionMode.Multiple) return;

    this.router.navigate([], {queryParams: {previous: event.id}, queryParamsHandling: 'merge'}).then(x => {

      this.router.navigate(["/" + AppRoutes.LocationModule + "/" + LocationRoutes.EventDetails], {
        queryParams: {
          id: event.id,
          last: this.last,
        }
      }).then();
    });
  }

  protected create() {
    // #664: the same refusal the hint line already shows, repeated at the gate. The submit button is disabled
    // in this state, but "disabled" is a rendering decision and this is the rule — a keyboard submit, a stale
    // allowance or a story driving the handler directly all have to hit it too.
    if (this.creationBlocked) {
      this.gas.createAndShow("Fehler", this.refusalReason ?? EVENT_CREATION_FALLBACK_MESSAGE, AlertLevel.error);
      return;
    }

    // The series is generated in the COMPANY zone (#658); resolve it first. Cached after the first call.
    this.companyTimeZone.load().subscribe(timeZone => this.createIn(timeZone));
  }

  /**
   * Builds the event(s) to create, in the company's time zone.
   *
   * A series keeps the first occurrence's company WALL CLOCK: an event at 20:00 Berlin repeats at 20:00 Berlin on
   * every day of the series, also across a DST change, and its weekday and "Wiederholen bis" day are company
   * calendar days (#658). The old loop added days to browser-local Dates, so both the time and the weekday
   * followed the ADMIN's browser zone. The instants sent to the server are unchanged in shape (ISO instants).
   */
  private createIn(timeZone: string) {
    if (this.newEvent.name.isNullOrWhitespace()) {
      this.gas.createAndShow("Fehler", "Name darf nicht leer sein.", AlertLevel.error);
      return;
    }

    if (this.newEvent.start >= this.newEvent.end) {
      this.gas.createAndShow("Fehler", "Ende muss nach Start sein.", AlertLevel.error);
      return;
    }

    const startWall = companyWallClock(this.newEvent.start, timeZone);
    const endWall = companyWallClock(this.newEvent.end, timeZone);
    /** The response deadline: the same company wall clock, `eventResponseDays` calendar days before `day`. */
    const responseEnd = (day: string, wall: CompanyWallClock): Date =>
      companyLocalTimeInstant(addCalendarDays(day, -(this.newEvent.options?.eventResponseDays ?? 0)), wall, timeZone);

    /**
     * The price profiles that actually go out — #664.
     *
     * A virtual event carries no `LocationConfigLinkers`, so they are stripped HERE, on the way out, rather
     * than deleted from the form model when the picker changes: the administrator keeps whatever they entered,
     * and switching back to "Kassen-Event" sends it after all.
     */
    const linkers = (): EventCreationLocationLinker[] | undefined =>
      this.isVirtual || !this.newEvent.configurations ? undefined
        : Object.entries(this.newEvent.configurations)
          .map(([location, configuration]) => new EventCreationLocationLinker(location, configuration));

    let events: EventCreationModel[] = [];
    if (!this.newEvent.repetition) {
      const response_end = responseEnd(startWall.day, startWall);

      let e = new EventCreationModel(this.newEvent.name, this.newEvent.start, this.newEvent.end,
        linkers(),
        !this.newEvent.options ? undefined
          : new EventCreationOptions(this.newEvent.options.isPublic, response_end,
            this.newEvent.options.hideInShiftMaker)
      )

      events.push(e);
    } else {
      if (this.newEvent.repetition.weekdays == 0) {
        this.newEvent.repetition.set(1 << mondayBasedWeekday(startWall.day));
      }

      // "Wiederholen bis" is a picker date: the calendar day the admin picked, inclusive.
      const lastDay = pickerDateToDayKey(this.newEvent.repetition.end);
      // How many calendar days the event spans (an event 22:00–04:00 ends on the next day).
      const spanDays = calendarDaysBetween(startWall.day, endWall.day);

      let configurations = linkers();

      for (let day = startWall.day; day <= lastDay; day = addCalendarDays(day, 1)) {
        if (!this.newEvent.repetition.contains(1 << mondayBasedWeekday(day))) continue;

        const start = companyLocalTimeInstant(day, startWall, timeZone);
        const end = companyLocalTimeInstant(addCalendarDays(day, spanDays), endWall, timeZone);
        // The name carries the company day; `dayKeyToPickerDate` makes the date pipe print exactly that day.
        const label = this.datePipe.transform(dayKeyToPickerDate(day), DateFormatLabels[this.newEvent.repetition.dateFormat]);

        let e = new EventCreationModel(`${label} ${this.newEvent.name}`, start, end,
          configurations,
          !this.newEvent.options ? undefined
            : new EventCreationOptions(this.newEvent.options.isPublic, responseEnd(day, startWall),
              this.newEvent.options.hideInShiftMaker)
        )

        events.push(e);
      }
    }

    // #664: one pass over the finished batch rather than a sixth constructor argument, so the two
    // `new EventCreationModel(...)` call sites above stay exactly as they were.
    events.forEach(e => e.kind = this.newEvent.kind);

    const limit = 50;
    if (events.length > limit) {
      this.gas.createAndShow("Fehler", `Limit von ${limit} Events überschritten. Bitte Zeitraum verringern.`, AlertLevel.warning);
      return;
    }

    let grouped: { [key: number]: EventCreationModel[] } = {};
    grouped = events.reduce((acc, x) => {
      let day = 1 << mondayBasedWeekday(companyDayKey(x.start, timeZone));
      if (!acc[day]) acc[day] = [];
      acc[day].push(x);
      return acc;
    }, grouped)

    Object.keys(grouped).forEach(key => {
      grouped[parseInt(key)] = grouped[parseInt(key)].sort((a, b) => a.start.getTime() - b.start.getTime());

      if (this.newEvent.repetition?.interval === Intervals.Weekly) {
        let evs = grouped[parseInt(key)];
        let first = evs[0];

        for (let i = 1; i < evs.length; i++) {
          // Whole weeks between the first and this occurrence, counted in company calendar days (exact across DST).
          let weeks = calendarDaysBetween(companyDayKey(first.start, timeZone), companyDayKey(evs[i].start, timeZone)) / 7;
          if (weeks % this.newEvent.repetition.intervalCount != 0) {
            evs.splice(i, 1);
            i--;
          }
        }
      }
    })

    if (!this.newEvent.repetition) this.create_send(grouped);
    else this.preview(grouped);
  }

  /**
   * Opens the series preview — but only after the server has said what it *would* create (#664).
   *
   * A series can straddle a contract boundary, and the occurrences on the 0-device side come back as virtual
   * ones. Kind is immutable once written, so the only useful moment to show that is before the batch is
   * persisted; `AddMany?dryRun=true` answers exactly that, writing nothing.
   *
   * A refused dry run never opens the preview: the refusal is the answer, and it is the same refusal the real
   * submit would produce a click later.
   */
  protected preview(grouped: { [key: number]: EventCreationModel[] }) {
    const events: EventCreationModel[] = [];
    Object.values(grouped).forEach(x => events.push(...x));

    if (events.length === 0) {
      this.gas.createAndShow("Fehler", "Keine Events erstellt.", AlertLevel.error);
      return;
    }

    this.eventHttp.addMany(events, true)
      .withLoadingIndicator()
      .subscribe({
        next: resolved => {
          this.dryRunVerdicts = this.verdictsOf(events, resolved);
          this.new_events_grouped = grouped;
        },
        error: err => {
          console.error(err);
          this.gas.createAndShow("Fehler", describeEventCreationError(err), AlertLevel.error);
        }
      });
  }

  /**
   * Matches the dry run's answer back onto the requested occurrences.
   *
   * Matched **by name**, not by index: every occurrence of a series carries its own date prefix, so the names
   * are unique, and a server that reorders the batch would silently shift an index-based mapping by one — a
   * preview that labels the wrong day is worse than no preview.
   *
   * There is deliberately no "this one would be refused" verdict. The server refuses a dry run as a WHOLE
   * batch (a structured 409, which {@link preview} turns into the refusal message and no preview at all), so a
   * per-occurrence refusal is a state that cannot occur — and a branch that cannot occur is a branch nobody
   * can trust. An occurrence the answer does not mention simply gets no verdict and renders as its bare name.
   */
  private verdictsOf(requested: EventCreationModel[], resolved: EventDto[]):
    Map<EventCreationModel, EventCreationVerdict> {
    const byName = new Map<string, EventDto>();
    resolved.forEach(event => byName.set(event.name, event));

    const verdicts = new Map<EventCreationModel, EventCreationVerdict>();
    requested.forEach(request => {
      const answer = byName.get(request.name);
      if (answer === undefined) return;

      // `kind` is the server's resolved value; `?? Real` covers a backend build that predates the field.
      const kind = answer.kind ?? EventKind.Real;
      verdicts.set(request, {kind, coerced: request.kind === EventKind.Real && kind === EventKind.Virtual});
    });

    return verdicts;
  }

  protected new_events_grouped_keys(): Weekdays[] {
    if (this.new_events_grouped === undefined) return [];
    return Object.keys(this.new_events_grouped).map(x => parseInt(x));
  }

  protected create_send(grouped: { [key: number]: EventCreationModel[] }) {
    let events: EventCreationModel[] = [];
    Object.values(grouped).forEach(x => events.push(...x));

    if (events.length === 0) {
      this.gas.createAndShow("Fehler", "Keine Events erstellt.", AlertLevel.error);
      return;
    }

    this.eventHttp.addMany(events).subscribe({
      next: events => {
        this.gas.createAndShow("Erfolg", "Events erfolgreich erstellt.", AlertLevel.success);
        this.new_events_grouped = undefined;
        this.dryRunVerdicts = undefined;
        // #664: the created events consumed virtual slots, so the hint line is now one answer behind.
        this.requestAllowance();
        this.loadEvents();
      },
      error: err => {
        // #664: branch on `err.error.code` and say which contract rule refused — never the server's English
        // `description`. Unknown codes and bare statuses fall back to the sentence this screen always showed.
        this.gas.createAndShow("Fehler",
          describeEventCreationError(err, "Fehler beim Erstellen der Events."), AlertLevel.error);
        console.error(err);
      }
    })
  }

  protected loadEvents(callback?: () => void) {
    // The cached full "all events" set (used for search) is now stale — invalidate it so the next
    // search re-fetches. NOTE: this loads the *display page* only and deliberately does NOT navigate
    // the route; page-size persistence lives in onPageSizeChange (Bug #146).
    this.searchAll = undefined;

    this.eventHttp.list(this.first, this.last).pipe(finalize(() => {
      this.applyFilter(this.search);
      setTimeout(() => {
        this.highlightPreviousEvent();
      })
    })).subscribe({
      next: events => {
        this.events_full = events;

        if (callback !== undefined) callback();
      },
      error: err => {
        this.gas.createAndShow("Fehler", "Fehler beim Laden der Events.", AlertLevel.error);
        console.error(err);
        this.events_full = null;
      }
    })
  }

  /**
   * Persists an explicitly-chosen page size ("Anzahl Einträge") to the URL and reloads the list. This is
   * the ONLY path that navigates the route — search must never navigate, so the search box keeps focus.
   */
  protected onPageSizeChange() {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {last: this.last},
      queryParamsHandling: 'merge'
    });
    this.loadEvents();
  }

  /**
   * Search entry point wired to the search box. Feeds the debounced pipe rather than filtering inline, so
   * rapid keystrokes don't thrash the view (the actual work happens in {@link applyFilter}).
   */
  protected filter(search: string) {
    this.search$.next(search);
  }

  /**
   * Applies the current search term to the visible list. An empty term shows the current page; a non-empty
   * term filters across the full event set (lazily fetched once via {@link loadAllForSearch}), so search
   * spans *all* events, not just the loaded page — without navigating the route or resizing the page.
   */
  private applyFilter(search: string) {
    this.search = search;

    // Page not loaded yet (or load errored) — mirror that state.
    if (this.events_full === undefined || this.events_full === null) {
      this.events = this.events_full;
      return;
    }

    // Empty search shows the current page as-is.
    if (this.search.isNullOrWhitespace()) {
      this.events = Object.assign([], this.events_full);
      return;
    }

    // Non-empty search spans ALL events. Fetch the full set once, then cache and re-filter.
    if (this.searchAll === undefined) {
      if (!this.searchAllLoading) this.loadAllForSearch();
      return; // applyFilter re-runs once the full set arrives
    }
    if (this.searchAll === null) return; // full-set load failed; keep the last results

    const term = this.search.toLowerCase();
    this.events = Object.assign([], this.searchAll.filter(event =>
      event.name.toLowerCase().includes(term) || event.publicTitle.toLowerCase().includes(term)));
  }

  /** Lazily loads the complete, unpaginated event set for search (no route navigation, no page resize). */
  private loadAllForSearch() {
    this.searchAllLoading = true;

    this.eventHttp.list(this.first, this.SEARCH_ALL_LIMIT).pipe(finalize(() => {
      this.searchAllLoading = false;
    })).subscribe({
      next: events => {
        this.searchAll = events;
        this.applyFilter(this.search); // now filter across the full set
      },
      error: err => {
        this.gas.createAndShow("Fehler", "Fehler beim Laden der Events.", AlertLevel.error);
        console.error(err);
        this.searchAll = null;
      }
    })
  }

  protected selectSupplier() {
    this.supplierPopup_isVisible = true;
  }

  protected download(supplier: SupplierDto | undefined) {
    if (this.selection.items.length === 0) {
      this.gas.createAndShow("Fehler", "Keine Events ausgewählt.", AlertLevel.error);
      return;
    }

    if (this.download_combined) {
      this.eventHttp.downloads(this.selection.items.map(x => x.id), supplier?.id ?? "")
        .withLoadingIndicator()
        .subscribe({
          next: file => {
            if (file.type == HttpEventType.Response) file.downloadFile();
          },
          error: err => {
            this.gas.createAndShow("Fehler", "Fehler beim Download der Events.", AlertLevel.error);
            console.error(err);
          }
        })
    } else {
      this.selection.items.forEach(event => {
        this.eventHttp.download(event.id, supplier?.id ?? "")
          .withLoadingIndicator()
          .subscribe({
            next: file => {
              if (file.type == HttpEventType.Response) file.downloadFile();
            },
            error: err => {
              this.gas.createAndShow("Fehler", `Fehler beim Download des Events: ${event.name}`, AlertLevel.error);
              console.error(err);
            }
          })
      })
    }
  }

  private highlightPreviousEvent() {
    if (this.previous && this.events && this.events.findIndex(x => x.id == this.previous) > -1) {
      const element = document.getElementById(this.previous)
      if (element != null) {
        element.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "center"
        });
        element.classList.add("!bg-theme-primary-100");
      }
    }
  }

  private preload(callback?: () => void) {
    // Read the initial query params ONCE from the snapshot. Subscribing to route.queryParams here would
    // re-fire this callback on every navigation (e.g. persisting the page size in onPageSizeChange),
    // re-entrantly reloading the list mid-interaction — the churn that dropped focus from the search
    // box and read as a "reload" on the first keystroke (Bug #146).
    const params = this.route.snapshot.queryParams;
    this.last = params.last == undefined ? 10 : parseInt(params.last);
    this.previous = params.previous;

    if (callback !== undefined) callback();
  }
}
